import React, { useEffect, useState } from 'react';
import { brandBlue, textDark, border, white, background, container, sectionPadding, heroPadding, card } from '../styles/theme';
import BeforeAfterVideo from '../components/BeforeAfterVideo';

const AiQualityEnhancement = ({ config }) => {
  const [selectedPair, setSelectedPair] = useState(0);

  useEffect(() => {
    setSelectedPair(0);
  }, [config]);

  const featuresPrimary = [
    { name: "AI Upscale", desc: "Convert low‑res to HD or 4K. Regular for speed, Ultra for max quality." },
    { name: "AI Deep Clean", desc: "Remove noise, artifacts, and blur. Pair with Upscale for best results." },
    { name: "AI Motion Smoothing", desc: "Increase FPS for silky playback—achieve smooth 60 FPS." },
    { name: "AI Slow Motion", desc: "Natural slow‑mo up to 10× without stutter or artifacts." },
    { name: "AI Edge Sharpen", desc: "Crisper details with adjustable intensity." },
    { name: "AI Denoise", desc: "Clean night and low‑light shots." },
    { name: "AI Stabilization", desc: "Advanced correction for shaky footage (auto safe zoom when needed)." },
    { name: "AI Dust Buster", desc: "Automatic dirt/scratch removal for 35mm/16mm/8mm scans." },
    { name: "AI De‑interlace", desc: "Convert interlaced sources (e.g., 480i) to progressive." },
    { name: "AI Audio Denoise", desc: "Isolate voice and remove background noise." },
  ];

  const specs = [
    { name: "Resize", value: "To any specific resolution" },
    { name: "Encoding Quality", value: "Configurable (speed vs quality)" },
    { name: "Codecs", value: "H.264 / H.265" },
    { name: "Outputs", value: "MP4, MOV, MKV" },
  ];

  const upscalingPairs = Array.isArray(config.upscalingStreams) ? config.upscalingStreams : [];
  const currentPair = upscalingPairs[selectedPair] || {};

  return (
    <div className="min-h-screen bg-white" style={{ color: textDark }}>
      {/* Hero */}
      <section className={`mx-auto ${container} px-6 ${heroPadding}`}>
        <img src="https://www.bigbluemarble.com/img/marble.svg" alt="" aria-hidden="true" role="presentation" className="pointer-events-none select-none absolute inset-0 -z-10 opacity-10 object-cover" />
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight" style={{ color: brandBlue }}>
              AI Video Quality Enhancement
              <span className="block text-lg md:text-2xl mt-3 font-normal" style={{ color: textDark, opacity: 0.85 }}>Transform legacy content with AI-powered enhancement to 4K/60fps broadcast quality</span>
            </h1>
            <p className="mt-4 max-w-2xl" style={{ opacity: 0.9 }}>
              Do you have old VOD assets that didn’t age well? We can bring them back to life. Watch our demo below to see how we utilized AI to improve the framerate and resolution, remove noise, artifacts, and blur, and make the picture look sharp and ready for streaming.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              {['4K Upscale','60 FPS','Deep Clean Ultra','Audio Denoise'].map((t,i)=> (
                <span key={i} className="rounded-full px-3 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ border: `1px solid ${border}` }}>
              <div className="aspect-video">
                <img src="/images/video_enhancement_poster.png" alt="Video Upscaling Poster" className="h-full w-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Split */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight" style={{ color: brandBlue }}>Featured Demos</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {/* 1/3 width */}
          <article className="md:col-span-1 group rounded-2xl overflow-hidden transition" style={{ background: white, border: `1px solid ${border}`, boxShadow: card }}>
            <div className="p-5" style={{ color: textDark }}>
              <h3 className="font-medium mb-3" style={{ color: brandBlue }}>Select Clip Pair</h3>
              <label className="block text-sm font-medium mb-2">Pair:</label>
              <select
                onChange={(e) => setSelectedPair(parseInt(e.target.value, 10))}
                value={selectedPair}
                className="w-full p-3 border rounded-lg"
                style={{ borderColor: border }}
              >
                {upscalingPairs.map((pair, idx) => (
                  <option key={idx} value={idx}>{pair.title || `Pair ${idx + 1}`}</option>
                ))}
              </select>

              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {[
                  'Deep Clean Ultra',
                  'Edge Sharpen',
                  'Stabilization',
                  'Denoise',
                  'Motion Smoothing',
                ].map((b, j) => (
                  <span key={j} className="rounded-full px-2.5 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>{b}</span>
                ))}
              </div>
            </div>
          </article>
          {/* 2/3 width */}
          <article className="md:col-span-2 group rounded-2xl overflow-hidden transition" style={{ background: white, border: `1px solid ${border}`, boxShadow: card }}>
            <div className="relative">
              <div className="aspect-video" style={{ background: background, borderBottom: `1px solid ${border}` }}>
                <BeforeAfterVideo
                  leftSrc={currentPair.baseUrl}
                  rightSrc={currentPair.upscaledUrl}
                  leftLabel="Original"
                  rightLabel="Upscaled"
                />
              </div>
            </div>
            <div className="p-5" style={{ color: textDark }}>
              <h3 className="font-medium" style={{ color: brandBlue }}>{currentPair.title || 'Upscaling Comparison'}</h3>
              <div className="mt-2 text-xs" style={{ opacity: 0.75 }}>
                Drag the slider to compare.
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight" style={{ color: brandBlue }}>VOD materials — many possibilities</h2>
            <ul className="mt-5 grid sm:grid-cols-2 gap-4">
              {featuresPrimary.map((f, i) => (
                <li key={i} className="rounded-2xl p-5" style={{ background: white, border: `1px solid ${border}`, boxShadow: card, color: textDark }}>
                  <div className="font-medium" style={{ color: brandBlue }}>{f.name}</div>
                  <div className="text-sm mt-1" style={{ opacity: 0.85 }}>{f.desc}</div>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl p-5" style={{ background: white, border: `1px solid ${border}`, boxShadow: card, color: textDark }}>
              <h3 className="font-semibold" style={{ color: brandBlue }}>Delivery & Formats</h3>
              <ul className="mt-3 space-y-2 text-sm">
                {specs.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                    <span><span style={{ opacity: 0.7 }}>{s.name}:</span> {s.value}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-xl p-4 text-xs" style={{ background: background, border: `1px dashed ${border}`, color: textDark }}>
                Regular = faster turnaround · Ultra = maximum quality
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6" style={{ background: brandBlue }}>
        <div className={`mx-auto ${container} text-center`}>
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Transform Your Legacy Content?
          </h2>
          <p className="text-xl mb-8 text-white opacity-90">
            Bring us your toughest footage. We'll upscale, clean, and deliver broadcast-ready results with AI-powered enhancement.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="https://www.bigbluemarble.com/en/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{ background: white, color: brandBlue, textDecoration: 'none' }}
            >
              Get in touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AiQualityEnhancement;
